import Media from 'react-media';
import PropTypes from 'prop-types';
import { vars } from '@etg/wings';
import { Icon, LoadingDots } from '@eti/components';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import {
  AIRLINES,
  NUMBER_OF_STOPS,
  PRICE,
  TRAVEL_TIME,
  TRIP_0_ARRIVAL_TIME,
  TRIP_0_DEPARTURE_TIME,
  TRIP_1_ARRIVAL_TIME,
  TRIP_1_DEPARTURE_TIME,
  TRIP_2_ARRIVAL_TIME,
  TRIP_2_DEPARTURE_TIME,
  TRIP_3_ARRIVAL_TIME,
  TRIP_3_DEPARTURE_TIME,
  TRIP_4_ARRIVAL_TIME,
  TRIP_4_DEPARTURE_TIME,
} from '../../../../../constants/searchFiltersConstants';
import { SelectedFilters } from '../../../constants/resultPropTypes';
import NumOfFlightsLabel from '../NumOfFlightsLabel';
import ResetButton from './ResetButton';

const headerStyles = css`
  display: flex;
  justify-content: space-between;
`;

const titleStyles = css`
  color: ${etiColors.black};
  font-size: 0.875rem;
  font-weight: 700;
`;

const controlsStyles = (isSearchLoading) => css`
  align-items: center;
  display: ${isSearchLoading ? 'none' : 'flex'};
`;

const controlButtonStyles = (isSearchLoading) => css`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: ${isSearchLoading ? 'none' : 'flex'};
  margin-left: 24px;
  outline: inherit;
  padding: 0;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 24px;
  }

  @media ${mediaQueries.medium.up} {
    margin-left: 16px;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 16px;
    }
  }
`;

const indicatorStyles = css`
  align-items: center;
  position: relative;
`;

const labelStyles = css`
  color: ${etiColors.black};
  display: inline-block;
  font-size: 0.875rem;
  padding-right: 15px;

  &::after {
    background-color: ${etiColors.info.solid};
    border-radius: 6px;
    content: '';
    display: inline-block;
    height: 8px;
    margin-left: 2px;
    position: absolute;
    top: 2px;
    width: 8px;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 2px;
    }
  }

  [dir='rtl'] & {
    padding-left: 15px;
    padding-right: 0;
  }
`;

const buttonTextStyles = css`
  color: ${etiColors.black};
  font-size: 0.875rem;
  min-width: 55px;
  padding-left: 8px;

  [dir='rtl'] & {
    padding-left: 0;
    padding-right: 8px;
  }
`;

const filterContentStyles = (isSearchLoading) => css`
  align-items: center;
  display: flex;
  margin: ${isSearchLoading && '0 auto'};
`;

const loadingFilterHeaderStyles = css`
  text-align: center;
`;

const loadingFilterTextStyles = css`
  color: ${etiColors.black};
  font-size: 0.875rem;
`;

const loadingDotsContainerStyles = css`
  color: ${vars.colors.links.default};
`;

const useResultFilterTexts = () => {
  const { t } = useTranslation();

  const translatedTexts = {
    [AIRLINES]: t('Result.SearchFilters.Airlines.Text'),
    [NUMBER_OF_STOPS]: t('Result.SearchFilters.Stops.Text'),
    [PRICE]: t('Result.SearchFilters.Price.Text'),
    [TRAVEL_TIME]: t('Result.SearchFilters.TravelTime.Text'),
    [TRIP_0_ARRIVAL_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_0_DEPARTURE_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_1_ARRIVAL_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_1_DEPARTURE_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_2_ARRIVAL_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_2_DEPARTURE_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_3_ARRIVAL_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_3_DEPARTURE_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_4_ARRIVAL_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
    [TRIP_4_DEPARTURE_TIME]: t('Result.SearchFilters.DepartureArrival.Label'),
  };

  return { translatedTexts };
};

const ResultFiltersHeader = ({
  filteredFlightsCount,
  flightsCount,
  isOpen,
  isSearchLoading,
  onClick,
  onResetAll,
  selectedFilters,
}) => {
  const { t } = useTranslation();
  const { translatedTexts } = useResultFilterTexts();
  const headerLabel = isOpen ? t('Close') : t('Responsive.Air.Result.Filter.Button.Filter');

  const indicatorLabels = Object.entries(selectedFilters).reduce((acc, [key, value]) => {
    const label = translatedTexts[key] || key;
    return value !== null && !acc.includes(label) ? [...acc, label] : acc;
  }, []);

  return (
    <header className={headerStyles} data-testid="resultPage-filters-header">
      <div className={filterContentStyles(isSearchLoading)}>
        {isSearchLoading ? (
          <div className={loadingFilterHeaderStyles}>
            <LoadingDots
              containerStyle={loadingDotsContainerStyles}
              dataTestId="resultFilterHeader-loadingDots"
            />
            <p className={loadingFilterTextStyles}>{t('Result.SearchFilters.Loading.Text')}</p>
          </div>
        ) : (
          <>
            <span className={titleStyles} data-testid="resultPage-filters-text">
              {t('Responsive.Air.Result.Filter.Button.Filter')}
            </span>
            <NumOfFlightsLabel
              filteredFlightsCount={filteredFlightsCount}
              flightsCount={flightsCount}
            />
          </>
        )}
        {indicatorLabels.length > 0 && !isSearchLoading && (
          <ResetButton
            dataTestId="resultPage-filterHeader-allFilterResetButton-button"
            onClick={onResetAll}
          />
        )}
      </div>
      <div className={cx('controls', controlsStyles(isSearchLoading))}>
        <Media query={mediaQueries.medium.up}>
          {(matches) =>
            matches &&
            indicatorLabels.length > 0 && (
              <div
                className={indicatorStyles}
                data-testid="resultPage-filterHeader-selectedFiltersIndicator"
              >
                {indicatorLabels.map((value) => (
                  <div className={labelStyles} key={value}>
                    {value}
                  </div>
                ))}
              </div>
            )
          }
        </Media>
        <button
          className={controlButtonStyles(isSearchLoading)}
          data-testid="resultPage-toggleFiltersButton-button"
          onClick={onClick}
          type="button"
        >
          <Icon color={etiColors.grayScale[10]} id={isOpen ? 'close-menu' : 'filter'} size="1rem" />
          <Media query={mediaQueries.large.up}>
            {(matches) => (matches ? <span className={buttonTextStyles}>{headerLabel}</span> : '')}
          </Media>
        </button>
      </div>
    </header>
  );
};

ResultFiltersHeader.propTypes = {
  filteredFlightsCount: PropTypes.number,
  flightsCount: PropTypes.number,
  isOpen: PropTypes.bool,
  isSearchLoading: PropTypes.bool,
  onClick: PropTypes.func,
  onResetAll: PropTypes.func,
  selectedFilters: SelectedFilters,
};

export default ResultFiltersHeader;
